import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'

import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

import schema from './schema'
import useStyles from './styles'

const FinalizedDatesSection = ({ onSaved, standalone = false }, ref) => {
  const {
    FINALIZED_PERIOD_DEFAULT_AMOUNT: DEFAULT_AMOUNT,
    FINALIZED_PERIOD_DEFAULT_UNIT: DEFAULT_UNIT,
    FINALIZED_PERIOD_UNIT_ITEMS: UNIT_ITEMS,
    FINALIZED_PERIOD_UNLIMITED_UNIT: UNLIMITED_UNIT,
  } = constants.activities

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [unit, setUnit] = useState(DEFAULT_UNIT)
  const [amountError, setAmountError] = useState(null)
  const [amount, setAmount] = useState(DEFAULT_AMOUNT)
  const [initialValues, setInitialValues] = useState(null)

  const classes = useStyles()
  const snackbar = useSnackbar()

  useEffect(() => {
    let active = true

    const fetchConfiguration = async () => {
      try {
        const { data } = await service.dponet.kanbanConfigurations.get()
        if (!active) return
        const { kanbanConfiguration } = data
        const fetchedUnit = kanbanConfiguration.finalizedPeriodUnit
        const fetchedAmount =
          kanbanConfiguration.finalizedPeriodAmount || DEFAULT_AMOUNT
        setUnit(fetchedUnit)
        setAmount(fetchedAmount)
        setInitialValues({ unit: fetchedUnit, amount: fetchedAmount })
      } catch (error) {
        if (active) {
          snackbar.open({
            message: helpers.formatters.errorMessage(error),
            variant: 'error',
          })
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchConfiguration()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const unlimited = unit === UNLIMITED_UNIT

  const handleToggleUnlimited = (event) => {
    setUnit(event.target.checked ? UNLIMITED_UNIT : DEFAULT_UNIT)
    setAmountError(null)
  }

  const handleAmountChange = (event) => {
    setAmount(Number(event.target.value))
    setAmountError(null)
  }

  const isDirty =
    !!initialValues &&
    (unit !== initialValues.unit ||
      (!unlimited && amount !== initialValues.amount))

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isDirty) return

      if (!unlimited) {
        try {
          await schema.validate({ amount })
        } catch (validationError) {
          setAmountError(validationError.message)
          validationError.handled = true
          throw validationError
        }
      }

      setSaving(true)
      try {
        await service.dponet.kanbanConfigurations.update({
          finalizedPeriodUnit: unit,
          finalizedPeriodAmount: unlimited ? null : amount,
        })
        setInitialValues({ unit, amount })
        onSaved?.()
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
        throw error
      } finally {
        setSaving(false)
      }
    },
  }))

  return (
    <Box className={clsx(classes.root, { [classes.standalone]: standalone })}>
      <Box className={classes.fieldsRow}>
        <Box className={classes.titleWrapper}>
          <Typography className={classes.title}>Datas Finalizadas</Typography>
          <Typography className={classes.description}>
            Escolha por quanto tempo atividades concluídas continuarão sendo
            exibidas.
          </Typography>
        </Box>

        <Box className={classes.controlsGroup}>
          {!unlimited && (
            <>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                label="Quantidade"
                className={classes.amountField}
                value={amount}
                disabled={loading || saving}
                onChange={handleAmountChange}
                error={!!amountError}
                helperText={amountError}
                FormHelperTextProps={{ className: classes.amountHelperText }}
                inputProps={{ min: 1 }}
              />
              <TextField
                select
                variant="outlined"
                size="small"
                label="Unidade"
                className={classes.unitField}
                value={unit}
                disabled={loading || saving}
                onChange={(event) => setUnit(event.target.value)}
              >
                {UNIT_ITEMS.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <FormControlLabel
            className={classes.unlimitedLabel}
            control={
              <Checkbox
                color="primary"
                checked={unlimited}
                onChange={handleToggleUnlimited}
                disabled={loading || saving}
              />
            }
            label="Trazer tudo"
          />
        </Box>
      </Box>
    </Box>
  )
}

const ForwardedFinalizedDatesSection = forwardRef(FinalizedDatesSection)

ForwardedFinalizedDatesSection.propTypes = {
  onSaved: PropTypes.func,
  standalone: PropTypes.bool,
}

export default ForwardedFinalizedDatesSection
